import { useState, useEffect } from "react";
import type { CSSProperties } from "react";
import type { Slide } from "../types";
import { ArrowLeft, ArrowRight } from "./icons";
import { ImageModal } from "./ImageModal";

/** Responsive image gallery (1–3 slides per view) with arrows and dots.
 *  Clicking a slide opens the complete image in a lightbox modal. */
export function Carousel({ slides, title }: { slides: Slide[]; title?: string }) {
	const [index, setIndex] = useState(0);
	const [perView, setPerView] = useState(3);
	const [lightbox, setLightbox] = useState<Slide | null>(null);

	useEffect(() => {
		const calc = () => {
			const w = window.innerWidth;
			setPerView(w <= 700 ? 1 : w <= 1000 ? 2 : 3);
		};
		calc();
		window.addEventListener("resize", calc);
		return () => window.removeEventListener("resize", calc);
	}, []);

	const maxIndex = Math.max(0, slides.length - perView);
	const clamped = Math.min(index, maxIndex);

	useEffect(() => {
		if (index > maxIndex) setIndex(maxIndex);
	}, [maxIndex, index]);

	const offset = clamped * (100 / perView);

	return (
		<div className="carousel-wrap reveal">
			<div className="carousel-head">
				{title ? <div className="section__label">{title}</div> : <span />}
				<div className="carousel-controls">
					<button
						className="carousel-btn"
						onClick={() => setIndex((i) => Math.max(0, i - 1))}
						disabled={clamped === 0}
						aria-label="Anterior"
					>
						<ArrowLeft />
					</button>
					<button
						className="carousel-btn"
						onClick={() => setIndex((i) => Math.min(maxIndex, i + 1))}
						disabled={clamped >= maxIndex}
						aria-label="Siguiente"
					>
						<ArrowRight />
					</button>
				</div>
			</div>
			<div className="carousel-track-outer">
				<div className="carousel-track" style={{ transform: `translateX(-${offset}%)` }}>
					{slides.map((s, i) => (
						<div className="carousel-slide" key={i}>
							<button
								className="carousel-slide__img"
								onClick={() => setLightbox(s)}
								aria-label={s.label ? `Ampliar: ${s.label}` : "Ampliar imagen"}
							>
								<img
									src={s.src}
									alt={s.label || ""}
									loading="lazy"
									style={{
										objectPosition: s.objectPosition,
										"--scale": s.scale ? String(s.scale) : undefined,
									} as CSSProperties}
								/>
							</button>
							{s.year && <div className="carousel-slide__year">{s.year}</div>}
							{s.label && <div className="carousel-slide__label">{s.label}</div>}
							{s.sub && <div className="carousel-slide__sub">{s.sub}</div>}
						</div>
					))}
				</div>
			</div>
			{maxIndex > 0 && (
				<div className="carousel-dots">
					{Array.from({ length: maxIndex + 1 }).map((_, i) => (
						<button
							key={i}
							className={`carousel-dot${i === clamped ? " active" : ""}`}
							onClick={() => setIndex(i)}
							aria-label={`Ir a ${i + 1}`}
						/>
					))}
				</div>
			)}

			<ImageModal slide={lightbox} onClose={() => setLightbox(null)} />
		</div>
	);
}
